/**
 * O que o painel DIZ sobre o próprio dado — fora do componente, onde o teste
 * alcança.
 *
 * O risco que o v6 escreve para o painel de exceções é "tratar um painel
 * desatualizado como visão em tempo real". Por isso toda seção carrega a frase
 * de atualização, e "sem registro" é dito com todas as letras em vez de mostrar
 * zero, que se leria como "está tudo bem".
 */
import type { StatusEquipamento } from '@/lib/dominio';
import { formatarInteiro } from '@/lib/formato';

/** "agora", "há 40 min", "há 5 h", "há 3 dias". */
export function haQuanto(instante: string, agora: Date = new Date()): string {
  const minutos = Math.max(0, Math.floor((agora.getTime() - new Date(instante).getTime()) / 60_000));
  if (minutos < 2) return 'agora';
  if (minutos < 60) return `há ${formatarInteiro(minutos)} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${formatarInteiro(horas)} h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'há 1 dia' : `há ${formatarInteiro(dias)} dias`;
}

/** A frase de atualização que vai em toda seção do painel. */
export function textoDeAtualizacao(atualizadoEm: string | null, agora: Date = new Date()): string {
  return atualizadoEm === null ? 'Sem registro ainda' : `Último registro ${haQuanto(atualizadoEm, agora)}`;
}

/**
 * "1 tarefa", "7 tarefas": a tela sabe o número, então flexiona por ele, em vez
 * de escrever "tarefa(s)" (p.22, voz precisa). Zero vai no plural, como se fala.
 */
export function contagem(n: number, singular: string, plural: string): string {
  return `${formatarInteiro(n)} ${n === 1 ? singular : plural}`;
}

/** Itens por página do rodapé da tabela (p.18), os mesmos das outras listas. */
export const OPCOES_POR_PAGINA: readonly number[] = [10, 25, 50, 100];
export const PADRAO_POR_PAGINA = 10;

/**
 * A fatia da página atual, e o "Mostrando 1–10 de 42" do rodapé (p.18). Página
 * além do fim volta para a última: trocar o período pode encolher a lista.
 */
export function fatiarPagina<T>(
  linhas: readonly T[],
  pagina: number,
  porPagina: number,
): { readonly visiveis: readonly T[]; readonly pagina: number; readonly paginas: number; readonly primeiro: number; readonly ultimo: number } {
  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const atual = Math.min(Math.max(pagina, 1), paginas);
  const inicio = (atual - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);
  return { visiveis, pagina: atual, paginas, primeiro: inicio + 1, ultimo: inicio + visiveis.length };
}

/** Os dois status que tiram a máquina de serviço: o que o card chama de indisponível. */
const INDISPONIVEIS: readonly StatusEquipamento[] = ['Parada', 'Em manutenção'];

/**
 * Quantas máquinas estão paradas ou em manutenção AGORA, contadas pela
 * distribuição por status — que cobre a frota inteira.
 *
 * A lista `indisponiveis` o servidor corta nas 10 mais antigas, e o card
 * contava a lista: com 14 máquinas paradas, o painel dizia 10. É a lista
 * cortada lida como total, o erro que o H7 do doctor vigia nas rotas.
 */
export function maquinasIndisponiveis(porStatus: readonly { readonly status: string; readonly maquinas: number }[]): number {
  return porStatus
    .filter((s) => (INDISPONIVEIS as readonly string[]).includes(s.status))
    .reduce((soma, s) => soma + s.maquinas, 0);
}

/**
 * As exceções de combustível do painel, em quatro verificações: consumo fora da
 * faixa em 7 dias, cobrança divergente, tanque acima da capacidade e ajuste
 * esperando quem tem alçada. A mesa e o celular (M04) leem DAQUI — a conta
 * escrita em dois lugares é a conta que um dia diverge.
 */
export function excecoesDeCombustivel(c: {
  readonly foraDaFaixa7Dias: number;
  readonly cobrancasDivergentes: number;
  readonly tanquesAcimaDaCapacidade: number;
  readonly ajustesEsperando: number;
}): number {
  return c.foraDaFaixa7Dias + c.cobrancasDivergentes + c.tanquesAcimaDaCapacidade + c.ajustesEsperando;
}

/** Quantos dias desde uma data `aaaa-mm-dd` ou instante — para "aberta há 9 dias". */
export function diasDesde(instante: string, agora: Date = new Date()): number {
  const t = instante.length === 10 ? new Date(`${instante}T12:00:00`).getTime() : new Date(instante).getTime();
  return Math.max(0, Math.floor((agora.getTime() - t) / 86_400_000));
}
